<script setup lang="ts">
  import {
    DocumentModal,
    DocumentRevision,
    type RevisionTrainingSessionDisplayRow,
  } from '@/entities/Document';
  import {
    REVISION_TRAINING_SGQ_FLOW_LABEL,
    REVISION_TRAINING_TYPE_LABELS,
  } from '@/features/Document';
  import type {
    DocumentRevisionTrainingResponse,
    RevisionTrainingMember,
  } from '@/widgets/document-actions/model/revisionTrainingResponse.types';
  import { onMounted, ref, computed } from 'vue';
  import api from '@/shared/api';
  import type { ApiResponse } from '@/shared/lib';
  import { useCustomToast, formatInstantToPtBR, formatCivilDateToPtBR } from '@/shared/lib';
  import { CustomCheckbox } from '@/shared/ui';

  const toast = useCustomToast();

  const props = defineProps<{
    title: string;
    revision: DocumentRevision | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const trainingData = ref<DocumentRevisionTrainingResponse | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const trainingType = computed(() => trainingData.value?.training?.type ?? '');

  const trainingTypeLabel = computed(
    () => REVISION_TRAINING_TYPE_LABELS[trainingType.value] ?? '-'
  );

  const isSgqSplitTrainingType = computed(
    () =>
      trainingType.value === 'SGQ_DOCUMENT_FOCAL' ||
      trainingType.value === 'SGQ_DOCUMENT_HOMOLOGATION'
  );

  const sgqTrainingFlowLabel = computed(
    () => REVISION_TRAINING_SGQ_FLOW_LABEL[trainingType.value] ?? ''
  );

  const trainingTypeDisplayLabel = computed(() => {
    if (isSgqSplitTrainingType.value) {
      return `Documento do SGQ | ${sgqTrainingFlowLabel.value}`;
    }
    return trainingTypeLabel.value;
  });

  const approvalDate = computed(() =>
    formatInstantToPtBR(trainingData.value?.approvals?.[0]?.createdAt)
  );

  const collaborators = computed(() => {
    if (!trainingData.value?.training) return [];

    const members = trainingData.value.training.members || [];

    return members.map((member: RevisionTrainingMember) => ({
      name: member.name,
      email: member.email,
      positions:
        (member.memberships ?? [])
          .map((m) => m.position.name)
          .filter((name) => name.length > 0)
          .join(', ') || '-',
    }));
  });

  const trainingSessionsRows = computed((): RevisionTrainingSessionDisplayRow[] => {
    const training = trainingData.value?.training;
    const sessions = training?.sessions;
    if (!sessions?.length) {
      return [];
    }

    return sessions.map((session) => ({
      id: session.id,
      dataLabel: formatCivilDateToPtBR(session.trainingDate),
      ministrante: session.responsibleUser?.name ?? '—',
      cargo: session.responsibleUserPosition?.name ?? '—',
    }));
  });

  const documentPrefix = computed(() => props.revision?.document?.prefix ?? '');

  const documentTitle = computed(() => props.revision?.document?.name ?? '');

  const documentRevisionDisplay = computed(() => {
    const rev = props.revision?.revision;
    if (rev == null) {
      return '';
    }
    return String(rev).padStart(2, '0');
  });

  const fetchTrainingData = async () => {
    if (!props.revision?.id) {
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const response = await api.get<ApiResponse<DocumentRevisionTrainingResponse>>(
        `/document-revisions/${props.revision.id}/training`
      );
      trainingData.value = response.data.data || null;
    } catch {
      toast.showErrorToast({
        type: 'error',
        title: 'Erro ao carregar dados de treinamento',
        message: 'Erro ao carregar dados de treinamento. Tente novamente.',
      });
      error.value = 'Erro ao carregar dados de treinamento';
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(() => {
    fetchTrainingData();
  });
</script>

<template>
  <DocumentModal
    :title="title"
    :revision="revision"
    :modal-title="title"
    :document-info-title="title.toLocaleUpperCase()"
    document-number="RQ18.1"
    document-revision-label="REV.07"
    :document-revision="revision?.revision"
    :has-document-prefix="false"
    @close="emit('close')"
  >
    <template #default>
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="text-sm text-gray-500">Carregando dados de treinamento...</div>
      </div>

      <div v-else-if="error" class="flex justify-center items-center py-12">
        <div class="text-sm text-red-500">
          {{ error }}
        </div>
      </div>

      <!-- Com treinamento -->
      <div
        v-else-if="trainingData?.hasTraining && trainingData?.training"
        class="flex flex-col !w-full"
      >
        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div class="mb-6">
          <div class="font-semibold flex text-sm mb-2">Será necessário treinamento?</div>
          <span
            class="w-1/8 border border-border-color mt-3 rounded-2xl px-5 py-4 bg-gray-02/20 inline-block text-sm"
          >
            Sim
          </span>
        </div>

        <div class="mb-6">
          <div class="font-semibold flex text-sm mb-2">Tipo de treinamento</div>
          <div class="flex flex-row gap-3 mt-3">
            <div
              class="flex-1 min-w-0 rounded-2xl border border-border-color px-5 py-4 bg-gray-02/20 flex items-center"
            >
              <input
                type="text"
                class="!w-full bg-transparent border-none focus:outline-none text-sm"
                :value="trainingTypeDisplayLabel"
                readonly
              />
            </div>
          </div>
        </div>

        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div class="mb-6">
          <div class="font-semibold flex text-sm mb-2">Documento</div>
          <div class="grid grid-cols-3 gap-x-6">
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Prefixo</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentPrefix"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Título</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentTitle"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Revisão</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentRevisionDisplay"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>

        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div v-if="trainingData?.approvals?.length" class="mb-6">
          <div class="font-semibold flex text-sm text-[var(--theme-secondary-ink)] mb-4">Aprovação</div>
          <div class="grid grid-cols-3 gap-x-6">
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Responsável</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="trainingData?.approvals?.[0]?.approver?.name || ''"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Cargo</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="trainingData?.approvals?.[0]?.approver?.position?.name || ''"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Data</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="approvalDate"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>

        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div v-if="collaborators.length > 0" class="mb-6">
          <div class="font-semibold flex text-sm mb-2">
            Colaboradores selecionados para treinamento:
          </div>
          <div class="border border-border-color rounded-2xl p-5 bg-gray-02/20 w-full">
            <table class="w-3/5 border-collapse">
              <thead class="text-left">
                <tr>
                  <th class="font-semibold text-sm w-12" />
                  <th class="font-semibold text-sm">Colaborador</th>
                  <th class="font-semibold text-sm">Cargo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(collaborator, index) in collaborators" :key="index">
                  <td class="pt-3 flex items-center justify-center">
                    <CustomCheckbox
                      :id="`collaborator-${index}`"
                      :value="`collaborator-${index}`"
                      :checked="true"
                      :disabled="true"
                    />
                  </td>
                  <td class="pt-3 text-sm">
                    {{ collaborator.name }}
                  </td>
                  <td class="pt-3 text-sm">
                    {{ collaborator.positions }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="mt-4 text-sm text-gray-03 font-semibold">
            Total de colaboradores selecionados: {{ collaborators.length }}
          </div>
        </div>

        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div v-if="trainingSessionsRows.length > 0" class="mb-6 flex flex-col gap-6">
          <div v-for="row in trainingSessionsRows" :key="row.id" class="flex flex-row gap-6">
            <div class="flex flex-col w-full">
              <div class="font-semibold flex text-sm mb-2">Ministrante</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="row.ministrante"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col shrink-0">
              <div class="font-semibold flex text-sm mb-2">Cargo</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="row.cargo"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col shrink-0">
              <div class="font-semibold flex text-sm mb-2">Data do Treinamento</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="row.dataLabel"
                  readonly
                />
              </div>
            </div>
          </div>
        </div>

        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div v-if="trainingData.training.notes" class="mb-6">
          <div class="font-semibold flex text-sm mb-2">Observações</div>
          <div class="rounded-2xl border p-6 border-border-color bg-gray-02/20">
            <textarea
              class="!w-full bg-transparent border-none focus:outline-none text-sm resize-none"
              :value="trainingData.training.notes"
              readonly
              rows="3"
            />
          </div>
        </div>
      </div>

      <!-- Sem treinamento -->
      <div v-else class="flex flex-col !w-full">
        <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

        <div>
          <div class="font-semibold flex text-sm mb-2">Documento</div>
          <div class="grid grid-cols-3 gap-x-6 mb-6">
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Prefixo</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentPrefix"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Título</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentTitle"
                  readonly
                />
              </div>
            </div>
            <div class="flex flex-col col-span-1">
              <div class="font-semibold flex text-sm mb-2">Revisão</div>
              <div
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
              >
                <input
                  type="text"
                  class="!w-full bg-transparent border-none focus:outline-none text-sm"
                  :value="documentRevisionDisplay"
                  readonly
                />
              </div>
            </div>
          </div>
          <hr class="h-0.5 mb-6 !w-full" />

          <div class="grid grid-cols-6 gap-x-6 mb-6">
            <div class="flex flex-col col-span-2">
              <div class="font-semibold flex text-sm mb-2">Será necessário treinamento?</div>
              <span
                class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20 text-sm"
              >
                Não
              </span>
            </div>
            <div class="flex flex-col col-span-4">
              <div class="font-semibold flex text-sm mb-2">Justificativa</div>
              <div>
                <div
                  class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
                >
                  <input
                    type="text"
                    class="!w-full bg-transparent border-none focus:outline-none text-sm"
                    :value="trainingData?.approvals?.[0]?.justification || ''"
                    readonly
                  />
                </div>
              </div>
            </div>
          </div>

          <hr class="h-0.5 mb-6 !w-full border-t-1 border-border-color" />

          <div v-if="trainingData?.approvals?.length" class="mb-6">
            <div class="font-semibold flex text-sm text-[var(--theme-secondary-ink)] mb-4">Aprovação</div>
            <div class="grid grid-cols-3 gap-x-6">
              <div class="flex flex-col col-span-1">
                <div class="font-semibold flex text-sm mb-2">Responsável</div>
                <div
                  class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
                >
                  <input
                    type="text"
                    class="!w-full bg-transparent border-none focus:outline-none text-sm"
                    :value="trainingData?.approvals?.[0]?.approver?.name || ''"
                    readonly
                  />
                </div>
              </div>
              <div class="flex flex-col col-span-1">
                <div class="font-semibold flex text-sm mb-2">Cargo</div>
                <div
                  class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
                >
                  <input
                    type="text"
                    class="!w-full bg-transparent border-none focus:outline-none text-sm"
                    :value="trainingData?.approvals?.[0]?.approver?.position?.name || ''"
                    readonly
                  />
                </div>
              </div>
              <div class="flex flex-col col-span-1">
                <div class="font-semibold flex text-sm mb-2">Data</div>
                <div
                  class="rounded-2xl border flex items-center p-6 border-border-color bg-gray-02/20"
                >
                  <input
                    type="text"
                    class="!w-full bg-transparent border-none focus:outline-none text-sm"
                    :value="approvalDate"
                    readonly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </DocumentModal>
</template>

<style scoped>
  @media print {
    .modal-header {
      display: none;
    }

    .print-button {
      display: none;
    }
  }
</style>
